import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { ArtifactDescriptor } from './artifacts';

/** A request to open a transient artifact in the panel. */
export type OpenArtifactRequest = {
  type: string;
  artifactId: string;
  payload?: unknown;
  /**
   * Whether to focus (and expand the panel onto) this artifact after adding it.
   * Defaults to `true`. Pass `false` to add it to the list in the background — the
   * panel keeps the currently focused artifact instead of jumping to this one. Used
   * when surfacing an agent's referenced documents/agents as sibling tabs without
   * yanking the user off the agent they're viewing.
   */
  focus?: boolean;
};

/** A sub-view pushed onto an artifact's in-panel navigation stack. */
export type ArtifactPanelViewEntry = {
  id: string;
  title?: string;
  payload?: unknown;
};

export type PushViewOptions = {
  /** Replace the top entry when already drilled in (e.g. clicking another run). */
  replaceTop?: boolean;
};

export type ArtifactPanelContextValue = {
  /** Whether the artifact panel is expanded to fill the main body (desktop only). */
  isExpanded: boolean;
  /** Toggle expanded layout when `canExpand` is true. */
  toggleExpanded: () => void;
  /**
   * Expand the panel, idempotently — a no-op when already expanded or when
   * `canExpand` is false. Prefer this over {@link toggleExpanded} whenever the
   * caller wants the panel *open* rather than flipped: a view that needs the room
   * (a node config drilled in over the canvas) must not collapse the panel when it
   * happens to already be expanded.
   */
  expand: () => void;
  /** Desktop artifact panel with at least one artifact — expand control is available. */
  canExpand: boolean;
  /**
   * True when the panel holds artifacts but none of them is on screen — the split
   * was collapsed (desktop) or the user is on the chat tab (mobile). Surfaces
   * cohabiting with the panel (the chat composer) use this to offer a way back in
   * via {@link revealArtifacts}.
   */
  isHidden: boolean;
  /**
   * Re-open the panel onto the artifact that was last focused. No-op when the
   * panel has no artifacts.
   */
  revealArtifacts: () => void;
  /** Focus the artifact panel on the given artifact, expanding the panel if collapsed. */
  focusArtifact: (artifactId: string) => void;
  /**
   * Open an artifact in the panel and focus it. Lets a view rendered *inside* an
   * artifact — which sits in an isolated model scope with no access to the chat's
   * models — open a sibling artifact (e.g. clicking a workflow node opens its
   * config as a `'workflow-node'` artifact, or a resource link opens a dataset).
   *
   * The artifact shows immediately without a saved chat or a server round-trip.
   * In a conversation-backed panel, opens that name a workspace resource are also
   * registered on the chat so they outlive the mount and reach the model's active
   * context — see `artifactRegistrationFor`. No-op when the host panel doesn't
   * manage artifacts opened this way.
   */
  openArtifact: (request: OpenArtifactRequest) => void;
  /**
   * Whether {@link openArtifact} actually does anything here. It is a silent
   * no-op in panels that don't manage artifacts (a standalone editor page), so
   * a caller offering "open this elsewhere" has to check rather than assume —
   * otherwise the affordance is there and the click does nothing.
   */
  canOpenArtifacts: boolean;
  /** Remove an artifact from the panel (e.g. closing a node config returns to the canvas). */
  closeArtifact: (artifactId: string) => void;
  /**
   * Remove an artifact **only if** it's still a passive background tab — one opened
   * via {@link openArtifact} with `focus: false` and never focused or selected since.
   * No-op for tool/persisted artifacts and any tab the user has engaged with. Used to
   * prune an agent's reference tabs when a mention is removed, without ever dismissing
   * a tab the caller didn't itself surface.
   */
  closeBackgroundArtifact: (artifactId: string) => void;
  /**
   * Push a sub-view onto the focused artifact's in-panel stack (e.g. workflow runs
   * list → run detail). Operates on the currently focused artifact.
   */
  pushView: (entry: ArtifactPanelViewEntry, options?: PushViewOptions) => void;
  /** Pop one level from the focused artifact's stack. */
  popView: () => void;
  /** Clear the focused artifact's entire view stack (e.g. leaving a runs drill-down). */
  resetViewStack: () => void;
  /** Whether the focused artifact has views to go back to. */
  canPopView: boolean;
  /** Stack for the currently focused artifact (empty = root). */
  viewStack: readonly ArtifactPanelViewEntry[];
  /**
   * Artifact id of the focused tab, or null when the panel has no artifacts.
   * Artifact editors use this to mark only the active tab's model as targeted.
   */
  activeArtifactId: string | null;
};

const ArtifactPanelContext = createContext<ArtifactPanelContextValue | null>(
  null
);

export const useArtifactPanel = (): ArtifactPanelContextValue | null =>
  useContext(ArtifactPanelContext);

export const ArtifactPanelProvider = ArtifactPanelContext.Provider;

/**
 * Everything the unified in-panel bar needs to render
 * `<artifact> ▾ › <pushView stack>` and act as the artifact switcher. Provided
 * by the artifact panel *around the artifact views only* (not the primary chat
 * pane), so a resource's {@link ResourcePageShell} rendered inside the panel
 * replaces its own breadcrumbs with this trail. `null` everywhere else
 * (standalone pages, the primary pane, mobile), where the shell keeps its
 * normal bar.
 *
 * There is deliberately no crumb for the surface hosting the panel. That title
 * is already the current crumb of the page bar one row above, and repeating it
 * here cost ~110px of the narrowest bar in the app while linking to nothing —
 * its click only did anything in the expanded layout, which the expand toggle
 * and Esc already handle.
 */
export type PanelUnifiedChrome = {
  /** Every open artifact, for the switcher menu. */
  artifacts: readonly ArtifactDescriptor[];
  /** The focused artifact, whose title is the switcher trigger. */
  activeArtifactId: string | null;
  /** Switch the focused artifact. */
  selectArtifact: (artifactId: string) => void;
  /** The focused artifact's drill-in stack, rendered as trailing crumbs. */
  viewStack: readonly ArtifactPanelViewEntry[];
  /** Pop the focused artifact's stack down to `depth` entries (crumb click). */
  popToViewDepth: (depth: number) => void;
  /** Dismiss the panel. Does *not* remove any artifact from the chat. */
  closeActiveArtifact: () => void;
  /**
   * The surface the panel belongs to — for a chat, the conversation.
   *
   * Carries no name on purpose. The trail omits the host because the page bar
   * one row above already gives it, and repeating it costs ~110px of the
   * narrowest bar in the app. That held only while a single bar spanned the
   * window: with a bar per pane the artifact read as a second document beside
   * the chat rather than something inside it, and the crumb was worth its width.
   * The bar spans again, so it is not.
   */
  host?: PanelHost;
  /**
   * Set when this artifact *is* the page rather than half of a split.
   *
   * Nothing sits beside it and nothing sits above it, so the one bar on screen
   * has to carry what two used to. It leads with a way back, and it drops the
   * controls that only mean something in a split: hiding the panel has no pane
   * to uncover, and switching to a sibling would silently change what the URL
   * says you are looking at.
   *
   * Sharing needs no special case. A focus view passes no {@link PanelHost}, so
   * {@link useHostOwnsSharing} is false and the resource draws the Share it
   * always had — which grants the resource, not the conversation. That is the
   * narrower grant this page wants, and it arrives by removing something rather
   * than by adding a second Share.
   */
  /**
   * Open an artifact as its own page, when the host has a route for one.
   *
   * Absent on surfaces with no such route (the standalone editor pages'
   * transient panel), where the expand control falls back to the in-place
   * expanded layout it always drove.
   */
  openFocusView?: (artifactId: string) => void;
  focusView?: {
    /** Back to the conversation: history where there is any, its URL otherwise. */
    onBack: () => void;
    /**
     * Switch to a sibling artifact. Full view is addressed by URL, so this has
     * to move the address rather than the tab state — otherwise the switcher
     * would show one artifact while the location bar named another, and Back
     * would leave the page instead of undoing the switch.
     */
    onSelectArtifact: (artifactId: string) => void;
    /**
     * Share this artifact, and only it.
     *
     * The chat's Share grants the conversation and everything in it, which is
     * the wrong offer from a page showing one thing; this grants the resource
     * the artifact stands for. Supplied by the host rather than built here, the
     * way {@link PanelHost.accessControl} is: the panel knows which artifact is
     * on screen, the page knows what sharing means.
     *
     * The bar draws nothing for an artifact with no such resource. See
     * `shareableResourceForArtifact`.
     */
    onShare: (artifact: ArtifactDescriptor) => void;
  };
};

/** See {@link PanelUnifiedChrome.host}. */
export type PanelHost = {
  /**
   * A control reporting who can see the host — and therefore everything in the
   * panel, since here that is one grant. Mirrored into the artifact's bar so
   * both sides of the split report the same access.
   *
   * Deliberately not a second Share button — see {@link useHostOwnsSharing},
   * which is the other half of this: a host that publishes an access control
   * also *owns* sharing for everything in the panel, so resources rendered in it
   * draw no Share of their own.
   */
  accessControl?: ReactNode;
};

const PanelUnifiedChromeContext = createContext<PanelUnifiedChrome | null>(
  null
);

export const usePanelUnifiedChrome = (): PanelUnifiedChrome | null =>
  useContext(PanelUnifiedChromeContext);

export const PanelUnifiedChromeProvider = PanelUnifiedChromeContext.Provider;

/**
 * True when this resource is the whole page rather than half of a split.
 *
 * For framing only. `mode: 'artifact' | 'page'` on the resource pages conflates
 * three things — how the page is framed, whether it owns its own Share, and
 * whether it nests a transient panel — and the focus view splits them: it is an
 * artifact by provenance and a page by presentation. Flipping `mode` to 'page'
 * would give it the right card and, with it, a second Share beside the bar's.
 *
 * So the pages keep `mode: 'artifact'` and ask this instead, and only about
 * layout and width.
 */
export const useIsArtifactFocusView = (): boolean =>
  useContext(PanelUnifiedChromeContext)?.focusView !== undefined;

/**
 * True when the surface hosting this panel shares the panel's contents along
 * with itself, so a resource page rendered here must not draw a Share of its
 * own.
 *
 * The chat is that host. Its Share opens a dialog built by
 * `collectShareableChatResources`, which lists the conversation *and every
 * artifact in it* as individually selectable rows — so the Share a document or
 * folder would draw for itself is not a narrower action sitting beside the
 * chat's, it is one row of the very dialog the chat's opens. Two buttons, same
 * dialog, a few hundred pixels apart, in the narrowest bar in the app.
 *
 * Which way to resolve that is not a matter of taste, and it is worth writing
 * down because the obvious reference gets it backwards: Claude Desktop hides
 * the *chat's* actions when an artifact opens, because there the artifact panel
 * owns its own sharing. Here the relationship is inverted — the chat's Share is
 * the superset and the resource's is a subset of it — so hiding the chat's
 * would drop the only control that can share the conversation and keep the one
 * that cannot. The subset goes instead.
 *
 * Keyed off {@link PanelHost.accessControl} because publishing an access control
 * is exactly the claim being made: *this* surface's grant is the one that
 * governs what is in the panel. A panel with no such host — the standalone
 * editor pages' transient panel — leaves the resource's own Share alone, since
 * there is nothing else there to share it with.
 *
 * {@link PanelUnifiedChrome.focusView} counts too, by a different route. There
 * the bar carries a Share for the artifact itself, so the resource's own is not
 * a subset of anything — it is the same grant, drawn twice.
 */
export const useHostOwnsSharing = (): boolean => {
  const chrome = useContext(PanelUnifiedChromeContext);
  // Two ways something else on this screen already shares what is in the panel,
  // and a resource that draws its own Share on top of either gets two buttons a
  // few pixels apart. The folder page is where that showed: it is the only page
  // that consults this, so it is the only one that *has* a Share of its own to
  // suppress, and in the focus view it drew one right beside the bar's.
  //
  // A host publishing an access control claims its grant governs the panel.
  // The focus view makes the narrower claim, and makes it in the bar: that Share
  // grants exactly the artifact on screen, which is what a resource's own Share
  // would have granted anyway. Either way the resource has nothing to add.
  return (
    chrome?.host?.accessControl !== undefined || chrome?.focusView !== undefined
  );
};

/**
 * True once an ancestor has already rendered the unified bar, so a nested
 * {@link ResourcePageShell} renders its content bare instead of stacking a
 * second bar. The chrome itself stays in scope (breadcrumbs are still the
 * panel's), only the bar is claimed once.
 */
const UnifiedBarRenderedContext = createContext<boolean>(false);

export const useUnifiedBarRendered = (): boolean =>
  useContext(UnifiedBarRenderedContext);

export const UnifiedBarRenderedProvider = UnifiedBarRenderedContext.Provider;

/**
 * The element in the unified bar's action zone that a *nested*
 * {@link ResourcePageShell} portals its own actions into.
 *
 * A nested shell renders no bar of its own (the ancestor claimed it), which
 * used to drop its actions on the floor — drilling workflow → agent left the
 * agent with no model selector and no save indicator while you were editing it.
 * Portalling puts them back in the one bar instead of stacking a second row.
 */
const PanelBarActionsSlotContext = createContext<HTMLElement | null>(null);

export const usePanelBarActionsSlot = (): HTMLElement | null =>
  useContext(PanelBarActionsSlotContext);

export const PanelBarActionsSlotProvider = PanelBarActionsSlotContext.Provider;

/**
 * Where a nested shell puts a control belonging to the artifact's *name* rather
 * than to the panel: the favorites star.
 *
 * A second slot rather than reusing the actions one, because the two land in
 * opposite ends of the bar. Every resource opened in the panel renders as a
 * nested shell (the panel drew the bar before the resource mounted), so without
 * this the star was portalled into the right-hand cluster and sat among the
 * save state and the model selector, a bar's width from the artifact it marks.
 */
const PanelBarTitleSlotContext = createContext<HTMLElement | null>(null);

export const usePanelBarTitleSlot = (): HTMLElement | null =>
  useContext(PanelBarTitleSlotContext);

export const PanelBarTitleSlotProvider = PanelBarTitleSlotContext.Provider;

/**
 * Overrides {@link useActiveArtifactId} for a subtree. Use when a stack view
 * hosts another resource editor (e.g. workflow → agent) so that editor's model
 * is targeted even though the panel tab is still the parent artifact.
 */
const ActiveArtifactIdOverrideContext = createContext<string | null>(null);

export const ActiveArtifactIdOverride = ({
  artifactId,
  children,
}: {
  artifactId: string;
  children: ReactNode;
}): ReactElement => (
  <ActiveArtifactIdOverrideContext.Provider value={artifactId}>
    {children}
  </ActiveArtifactIdOverrideContext.Provider>
);

/**
 * Focused artifact id when rendered inside an {@link ArtifactPanelProvider},
 * otherwise null (standalone resource pages should treat that as "always target").
 * Honors {@link ActiveArtifactIdOverride} when set.
 */
export const useActiveArtifactId = (): string | null => {
  const override = useContext(ActiveArtifactIdOverrideContext);
  const panel = useArtifactPanel();
  if (override !== null) {
    return override;
  }
  if (panel === null) {
    return null;
  }
  return panel.activeArtifactId;
};
